import React, { useState, useMemo } from 'react';
import {
    BarChart, Bar, LineChart, Line, RadarChart, Radar,
    PolarGrid, PolarAngleAxis, PolarRadiusAxis,
    XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { useBankLens } from './BankLensContext';
import styles from './BankLensPage.module.css';

/* ── Tooltip renderer ─────────────────────────────────────── */
function ChartTip({ active, payload, label, suffix = '%' }) {
    if (!active || !payload?.length) return null;
    return (
        <div className={styles.rechartsTip}>
            <div className={styles.rechartsTipLabel}>{label}</div>
            {payload.map((p, i) => (
                <div key={i} className={styles.rechartsTipRow}>
                    <span className={styles.rechartsTipDot} style={{ background: p.color }} />
                    <span className={styles.rechartsTipName}>{p.name}</span>
                    <span className={styles.rechartsTipVal}>{p.value != null ? `${p.value.toFixed(2)}${suffix}` : '—'}</span>
                </div>
            ))}
        </div>
    );
}

/* ── Cross-bank bar chart tabs ────────────────────────────── */
const BAR_TABS = [
    { key: 'profitability', label: 'Profitability', metrics: ['nim', 'roa', 'roe'] },
    { key: 'quality', label: 'Asset Quality', metrics: ['gnpa', 'nnpa', 'pcr'] },
    { key: 'capital', label: 'Capital', metrics: ['car', 'cet1'] },
    { key: 'valuation', label: 'Valuation', metrics: ['pe', 'eps'] },
];
const BAR_COLORS = ['#20b072', '#38bdf8', '#f59e0b', '#a78bfa'];

export default function BankLensOverview({ onSelectBank }) {
    const { banks: BANKS, getLatestMetrics, getMetrics, currentQuotes: CURRENT_QUOTES, METRIC_INFO, fmtMktCap, summary } = useBankLens();
    const [barTab, setBarTab] = useState('profitability');

    /* ── Derived data ───────────────────────────── */
    const allLatest = useMemo(() => BANKS.map(b => getLatestMetrics(b.ticker)), [BANKS, getLatestMetrics]);

    const totalMktCap = useMemo(() => summary.totalMarketCap, [summary]);
    const avgROE = useMemo(() => {
        if (summary.avgROE != null) return summary.avgROE;
        const vals = allLatest.filter(d => d.roe != null).map(d => d.roe);
        return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
    }, [allLatest, summary]);
    const avgGNPA = useMemo(() => {
        if (summary.avgGNPA != null) return summary.avgGNPA;
        const vals = allLatest.filter(d => d.gnpa != null).map(d => d.gnpa);
        return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
    }, [allLatest, summary]);

    /* Bar chart data */
    const activeBarTab = BAR_TABS.find(t => t.key === barTab);
    const barData = useMemo(() => {
        return BANKS.map(b => {
            const d = getLatestMetrics(b.ticker);
            const row = { name: b.shortName };
            activeBarTab.metrics.forEach(m => { row[m] = d[m]; });
            return row;
        });
    }, [activeBarTab, BANKS, getLatestMetrics]);

    /* NIM trend (last 6 years) */
    const nimTrend = useMemo(() => {
        const years = [2021, 2022, 2023, 2024, 2025, 2026];
        return years.map(y => {
            const row = { year: `FY${y}` };
            BANKS.forEach(b => {
                const data = getMetrics(b.ticker);
                const match = data.find(d => d.y === y);
                if (match) row[b.shortName] = match.nim;
            });
            return row;
        });
    }, [BANKS, getMetrics]);

    /* Radar data (top 5 by market cap) */
    const radarBanks = useMemo(() => {
        const sorted = [...BANKS].sort((a, b) => {
            const qa = CURRENT_QUOTES[a.ticker]?.marketCap || 0;
            const qb = CURRENT_QUOTES[b.ticker]?.marketCap || 0;
            return qb - qa;
        });
        return sorted.slice(0, 5);
    }, [BANKS, CURRENT_QUOTES]);

    const radarData = useMemo(() => {
        const dims = [
            { key: 'roa', label: 'ROA', max: 4 },
            { key: 'roe', label: 'ROE', max: 25 },
            { key: 'nim', label: 'NIM', max: 6 },
            { key: 'pcr', label: 'PCR', max: 100 },
            { key: 'car', label: 'CAR', max: 30 },
        ];
        return dims.map(dim => {
            const row = { metric: dim.label };
            radarBanks.forEach(b => {
                const d = getLatestMetrics(b.ticker);
                row[b.shortName] = d[dim.key] != null
                    ? Math.min((d[dim.key] / dim.max) * 100, 100)
                    : 0;
            });
            return row;
        });
    }, [radarBanks, getLatestMetrics]);

    return (
        <>
            {/* ── Header ──── */}
            <div className={styles.mainHeader}>
                <h1 className={styles.mainTitle}>Portfolio Overview</h1>
                <p className={styles.mainSubtitle}>
                    9 banks tracked · FY2016–Q3 FY26 · All metrics pre-computed
                </p>
            </div>

            {/* ── Summary cards ──── */}
            <div className={styles.portfolioSummary}>
                <div className={styles.summaryCard}>
                    <div className={styles.summaryLabel}>Total Market Cap</div>
                    <div className={styles.summaryValue}>{fmtMktCap(totalMktCap)}</div>
                    <div className={styles.summarySub}>9 banks combined</div>
                </div>
                <div className={styles.summaryCard}>
                    <div className={styles.summaryLabel}>Avg ROE</div>
                    <div className={styles.summaryValue}>{avgROE.toFixed(1)}%</div>
                    <div className={styles.summarySub}>
                        <span className={avgROE >= 15 ? styles.positive : avgROE >= 10 ? styles.neutral : styles.negative}>
                            {avgROE >= 15 ? '● Healthy' : avgROE >= 10 ? '● Moderate' : '● Below target'}
                        </span>
                    </div>
                </div>
                <div className={styles.summaryCard}>
                    <div className={styles.summaryLabel}>Avg GNPA</div>
                    <div className={styles.summaryValue}>{avgGNPA.toFixed(2)}%</div>
                    <div className={styles.summarySub}>
                        <span className={avgGNPA <= 2.5 ? styles.positive : avgGNPA <= 4 ? styles.neutral : styles.negative}>
                            {avgGNPA <= 2.5 ? '● Clean' : avgGNPA <= 4 ? '● Moderate' : '● Elevated'}
                        </span>
                    </div>
                </div>
            </div>

            {/* ── Bank tiles ──── */}
            <div className={styles.tilesGrid}>
                {BANKS.map(b => {
                    const d = getLatestMetrics(b.ticker);
                    return (
                        <div
                            key={b.ticker}
                            className={styles.tile}
                            style={{ borderLeftColor: b.color }}
                            onClick={() => onSelectBank(b.ticker)}
                        >
                            <div className={styles.tileHeader}>
                                <span className={styles.tileName}>{b.shortName}</span>
                                <span
                                    className={styles.tileType}
                                    style={{
                                        background: b.type === 'PVT' ? 'rgba(32,176,114,0.15)' : 'rgba(56,189,248,0.15)',
                                        color: b.type === 'PVT' ? '#20b072' : '#38bdf8',
                                    }}
                                >
                                    {b.type}
                                </span>
                            </div>
                            <div>
                                <span className={styles.tilePrice}>₹{d.price?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                                <span className={`${styles.tileChange} ${d.changePct >= 0 ? styles.positive : styles.negative}`}>
                                    {d.changePct >= 0 ? '▲' : '▼'} {Math.abs(d.changePct).toFixed(2)}%
                                </span>
                            </div>
                            <div className={styles.tileMetrics}>
                                <div className={styles.tileMetricRow}>
                                    <span className={styles.tileMetricLabel}>ROE</span>
                                    <span className={`${styles.tileMetricValue} ${d.roe >= 15 ? styles.positive : d.roe >= 10 ? styles.neutral : styles.negative}`}>
                                        {d.roe?.toFixed(1)}%
                                    </span>
                                </div>
                                <div className={styles.tileMetricRow}>
                                    <span className={styles.tileMetricLabel}>GNPA</span>
                                    <span className={`${styles.tileMetricValue} ${d.gnpa <= 2.5 ? styles.positive : d.gnpa <= 4 ? styles.neutral : styles.negative}`}>
                                        {d.gnpa?.toFixed(2)}%
                                    </span>
                                </div>
                                <div className={styles.tileMetricRow}>
                                    <span className={styles.tileMetricLabel}>ROA</span>
                                    <span className={`${styles.tileMetricValue} ${d.roa >= 1 ? styles.positive : d.roa >= 0.5 ? styles.neutral : styles.negative}`}>
                                        {d.roa?.toFixed(2)}%
                                    </span>
                                </div>
                                <div className={styles.tileMetricRow}>
                                    <span className={styles.tileMetricLabel}>NIM</span>
                                    <span className={`${styles.tileMetricValue} ${d.nim >= 3.5 ? styles.positive : d.nim >= 2.5 ? styles.neutral : styles.negative}`}>
                                        {d.nim?.toFixed(2)}%
                                    </span>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* ── Cross-bank chart + Radar + NIM trend ──── */}
            <div className={styles.chartsRowTriple}>
                {/* Bar chart */}
                <div className={styles.chartContainer}>
                    <div className={styles.cardTitle}>Cross-Bank Comparison</div>
                    <div className={styles.tabsBar}>
                        {BAR_TABS.map(t => (
                            <button
                                key={t.key}
                                className={barTab === t.key ? styles.tabActive : styles.tab}
                                onClick={() => setBarTab(t.key)}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>
                    <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={barData} barGap={2} barSize={14}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#7a8698' }} />
                            <YAxis tick={{ fontSize: 10, fill: '#7a8698' }} width={40} />
                            <Tooltip content={<ChartTip suffix={barTab === 'valuation' ? 'x' : '%'} />} />
                            {activeBarTab.metrics.map((m, i) => (
                                <Bar key={m} dataKey={m} fill={BAR_COLORS[i]} radius={[3, 3, 0, 0]}
                                    name={METRIC_INFO[m]?.label || m.toUpperCase()} />
                            ))}
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                {/* Radar chart */}
                <div className={styles.chartContainerSmall}>
                    <div className={styles.cardTitle}>Quality Radar</div>
                    <ResponsiveContainer width="100%" height={200}>
                        <RadarChart data={radarData} outerRadius="75%">
                            <PolarGrid stroke="#1e2a42" />
                            <PolarAngleAxis dataKey="metric" tick={{ fontSize: 10, fill: '#7a8698' }} />
                            <PolarRadiusAxis tick={false} axisLine={false} domain={[0, 100]} />
                            {radarBanks.map(b => (
                                <Radar
                                    key={b.ticker}
                                    name={b.shortName}
                                    dataKey={b.shortName}
                                    stroke={b.color}
                                    fill={b.color}
                                    fillOpacity={0.08}
                                    strokeWidth={1.5}
                                />
                            ))}
                            <Tooltip content={<ChartTip suffix="" />} />
                        </RadarChart>
                    </ResponsiveContainer>
                </div>

                {/* NIM trend */}
                <div className={styles.chartContainerSmall}>
                    <div className={styles.cardTitle}>NIM Trend (6Y)</div>
                    <ResponsiveContainer width="100%" height={200}>
                        <LineChart data={nimTrend}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="year" tick={{ fontSize: 9, fill: '#7a8698' }} />
                            <YAxis tick={{ fontSize: 10, fill: '#7a8698' }} width={35} domain={['auto', 'auto']} />
                            <Tooltip content={<ChartTip />} />
                            {BANKS.map(b => (
                                <Line
                                    key={b.ticker}
                                    type="monotone"
                                    dataKey={b.shortName}
                                    stroke={b.color}
                                    strokeWidth={1.5}
                                    dot={false}
                                    name={b.shortName}
                                />
                            ))}
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* ── Scorecard table ──── */}
            <div className={styles.scorecardWrap}>
                <h3 className={styles.scorecardTitle}>Q3 FY26 Scorecard</h3>
                <table className={styles.scorecard}>
                    <thead>
                        <tr>
                            <th>Bank</th>
                            <th>NIM</th>
                            <th>ROA</th>
                            <th>ROE</th>
                            <th>C/I</th>
                            <th>GNPA</th>
                            <th>NNPA</th>
                            <th>PCR</th>
                            <th>CAR</th>
                            <th>CET1</th>
                            <th>Mkt Cap</th>
                            <th>P/E</th>
                        </tr>
                    </thead>
                    <tbody>
                        {BANKS.map(b => {
                            const d = getLatestMetrics(b.ticker);
                            return (
                                <tr key={b.ticker} onClick={() => onSelectBank(b.ticker)}>
                                    <td>
                                        <span className={styles.scorecardBankName}>
                                            <span className={styles.bankDot} style={{ background: b.color }} />
                                            {b.shortName}
                                        </span>
                                    </td>
                                    <td className={d.nim >= 3.5 ? styles.positive : ''}>{d.nim?.toFixed(1)}%</td>
                                    <td className={d.roa >= 1 ? styles.positive : ''}>{d.roa?.toFixed(2)}%</td>
                                    <td className={d.roe >= 15 ? styles.positive : ''}>{d.roe?.toFixed(1)}%</td>
                                    <td className={d.cti <= 50 ? styles.positive : styles.negative}>{d.cti?.toFixed(1)}%</td>
                                    <td className={d.gnpa <= 2.5 ? styles.positive : styles.negative}>{d.gnpa?.toFixed(2)}%</td>
                                    <td className={d.nnpa <= 0.5 ? styles.positive : ''}>{d.nnpa?.toFixed(2)}%</td>
                                    <td className={d.pcr >= 70 ? styles.positive : ''}>{d.pcr}%</td>
                                    <td className={d.car >= 15 ? styles.positive : ''}>{d.car?.toFixed(1)}%</td>
                                    <td>{d.cet1?.toFixed(1)}%</td>
                                    <td>{fmtMktCap(d.marketCap)}</td>
                                    <td>{d.pe?.toFixed(1)}x</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </>
    );
}
